<script setup lang="ts">
//#region imports
import { endOfDay, format, startOfDay } from 'date-fns';
import { cloneDeepWith } from 'lodash-es';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { isErrorResponse, type EmitsToListeners, type FormErrors } from '@/types/core';
import type { OverrideFields } from '@/types/util';
import type {
  GetAdminsByIdResponse,
  PutAdminsByIdData,
  PutAdminsByIdResponse,
} from '@/types/openapi-ts';
//#endregion

//#region types
type UserInfo = GetAdminsByIdResponse['payload'];
type EditableUserSource = Pick<
  UserInfo,
  | 'id'
  | 'name'
  | 'email'
  | 'timezone'
  | 'messenger'
  | 'messenger_id'
  | 'notes'
  | 'entities_visibility_from'
  | 'entities_visibility_to'
>;
type EditableDate = Date | string | null;
type UpdatedUser = PutAdminsByIdResponse['payload'];

type FormType = OverrideFields<PutAdminsByIdData['body'], {
  messenger: string | null;
  messenger_id: string | null;
  entities_visibility_from: EditableDate;
  entities_visibility_to: EditableDate;
}>;

interface Props {
  user: EditableUserSource;
}

interface Emits {
  close: [];
  refresh: [user: UpdatedUser];
}

export interface EditTeamsUserParams {
  data: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const errors = ref<FormErrors>({});
const form = ref<FormType>(createDefaultForm());
const isOverall = ref(false);
const isLoading = ref(false);
//#endregion

//#region computed
const timezones = computed(() => store.getters['misc/timezones']);
const messengers = computed(() => store.getters['misc/apiMessengers']);
const messengerPlaceholder = computed(() => {
  if (!form.value.messenger) {
    return t('team_popup.messenger_id_placeholder');
  }

  return messengers.value.find(
    (messenger) => messenger.id === form.value.messenger,
  )?.example_placeholder ?? '';
});
//#endregion

//#region methods
function createDefaultForm(): FormType {
  return {
    name: props.user.name,
    email: props.user.email,
    timezone: props.user.timezone,
    messenger: props.user.messenger,
    messenger_id: props.user.messenger_id,
    notes: props.user.notes,
    entities_visibility_from: props.user.entities_visibility_from,
    entities_visibility_to: props.user.entities_visibility_to,
  };
}

function setErrors(error: unknown) {
  if (!isErrorResponse(error)) {
    throw error;
  }

  errors.value = cloneDeepWith(error.data.errors) ?? {};
}

function open() {
  form.value = createDefaultForm();
  isOverall.value = form.value.entities_visibility_from === null
    && form.value.entities_visibility_to === null;
  isVisible.value = true;
}

function handleClose() {
  isVisible.value = false;
  errors.value = {};
  emit('close');
}

function formatDate(value: EditableDate, end: boolean) {
  if (value === null) {
    return null;
  }

  const date = typeof value === 'string' ? new Date(value) : value;
  return format(end ? endOfDay(date) : startOfDay(date), 'yyyy-MM-dd HH:mm:ss');
}

function handleSave() {
  if (!props.user.id) {
    return;
  }

  isLoading.value = true;
  errors.value = {};

  const data: PutAdminsByIdData['body'] = {
    ...form.value,
    name: form.value.name?.trim() ?? '',
    email: form.value.email?.trim() ?? '',
    messenger: form.value.messenger ?? '',
    messenger_id: form.value.messenger_id?.trim() ?? '',
    entities_visibility_from: isOverall.value
      ? null
      : formatDate(form.value.entities_visibility_from, false),
    entities_visibility_to: isOverall.value
      ? null
      : formatDate(form.value.entities_visibility_to, true),
  };

  api.putAdmins(props.user.id, data).then((response) => {
    emit('refresh', response.data.payload);
    handleClose();
  }).catch(setErrors).finally(() => {
    isLoading.value = false;
  });
}
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="t('team_popup.edit_user_title', { name: props.user.name })"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="form.name"
          :error="errors.name"
          :placeholder="t('team_popup.name_placeholder')"
          :label="t('team_popup.name_label')"
          size="big"
          autosize
        />
        <UiInput
          v-model="form.email"
          :error="errors.email"
          :placeholder="t('team_popup.email_placeholder')"
          :label="t('team_popup.email_label')"
          size="big"
          autosize
        />
        <UiSelectElement
          v-model="form.timezone"
          :placeholder="t('team_popup.timezone_placeholder')"
          :label="t('team_popup.timezone_label')"
          :list="timezones"
          option-value-param="zone"
          filterable
          size="big"
        />
        <div class="t:space-y-8">
          <div class="t:flex t:items-center">
            <UiLabel :label="t('team_popup.see_stats_label')" />
            <UiCheckbox
              v-model="isOverall"
              :label="t('team_popup.see_stats_overall')"
              class="t:ml-8"
            />
          </div>
          <div class="t:flex t:items-center">
            <UiDatePicker
              v-model="form.entities_visibility_from"
              class="t:w-full"
              :disabled="isOverall"
              :placeholder="t('team_popup.see_stats_placeholder')"
              :multi-calendars="false"
              :range="false"
              teleport
              auto-apply
              :always-clearable="false"
              :is-error="!!errors.entities_visibility_from"
              size="big"
            />
            <div class="t:mx-3">
              –
            </div>
            <UiDatePicker
              v-model="form.entities_visibility_to"
              class="t:w-full"
              :disabled="isOverall"
              :placeholder="t('team_popup.see_stats_placeholder')"
              :multi-calendars="false"
              :range="false"
              teleport
              auto-apply
              :always-clearable="false"
              :is-error="!!errors.entities_visibility_to"
              size="big"
            />
          </div>
          <UiHelp
            v-if="errors.entities_visibility_to?.[0]"
            :text="t(errors.entities_visibility_to[0].message)"
            is-error
          />
        </div>
        <div class="t:flex t:items-start t:space-x-16">
          <UiSelectElement
            v-model="form.messenger"
            :placeholder="t('team_popup.messenger_placeholder')"
            :label="t('team_popup.messenger_label')"
            :list="messengers"
            size="big"
          />
          <UiInput
            v-model="form.messenger_id"
            :error="errors.messenger_id"
            :placeholder="messengerPlaceholder"
            :disabled="!form.messenger"
            :label="t('team_popup.messenger_id_label')"
            size="big"
            autosize
          />
        </div>
        <UiTextarea
          v-model="form.notes"
          :label="t('team_popup.note_label')"
          :placeholder="t('team_popup.note_placeholder')"
          :rows="3"
          :error="errors.notes"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ t('team_popup.cancel_button') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ t('team_popup.save_button') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
